import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * A single-field "make it personal" CTA — enters DOB, saves to
 * localStorage (device-only, no server), and routes into your guidance.
 * Used on the landing page and anywhere we want to prompt sign-up-lite.
 */
const STORAGE_KEY = "ep_biorhythm_dob";

export default function PersonaliseCTA({ compact = false }) {
  const navigate = useNavigate();
  const [dob, setDob] = useState("");
  const [alreadySaved, setAlreadySaved] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setAlreadySaved(true);
        setDob(saved);
      }
    } catch { /* ignore */ }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!dob) { toast.error("Please enter your date of birth"); return; }
    try { localStorage.setItem(STORAGE_KEY, dob); } catch { /* quota */ }
    toast.success("You're all set — welcome to your guidance");
    navigate("/almanac");
  };

  return (
    <section
      className="relative border-t border-[color:var(--gold)]/10"
      style={{ background: "linear-gradient(180deg, rgba(212,162,76,0.06), transparent)" }}
      data-testid="personalise-section"
    >
      <div className="mx-auto max-w-[900px] px-6 md:px-10 py-24 md:py-32 text-center">
        <div className="label-overline">Make it yours · free, no account</div>
        <h2 className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight">
          Your daily reading, <span className="font-serif-italic text-[color:var(--gold)]">tuned to you.</span>
        </h2>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed max-w-xl mx-auto">
          Give us your date of birth once, and every time you open the guidance you&apos;ll see your personal biorhythm, the moon&apos;s dance with your sign, and a daily note tailored to <em>you</em> — not a generic horoscope for the whole zodiac.
        </p>
        <p className="mt-4 flex items-center justify-center gap-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">
          <ShieldCheck size={14} className="text-[color:var(--gold)]" />
          Kept on your device only · never sent to us · no login, no password.
        </p>

        {alreadySaved ? (
          <div className="mt-10" data-testid="personalise-saved">
            <p className="font-serif-italic text-[color:var(--parchment)] text-lg">
              We already have you — welcome back.
            </p>
            <Button
              onClick={() => navigate("/almanac")}
              className="mt-6 h-12 px-8 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
              data-testid="personalise-open-almanac"
            >
              Open my guidance <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto"
            data-testid="personalise-form"
          >
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="bg-white border-slate-300 rounded-full h-12 px-6 text-slate-900 font-sans-body flex-1"
              required
              data-testid="personalise-dob-input"
            />
            <Button
              type="submit"
              className="h-12 px-7 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
              data-testid="personalise-submit"
            >
              <Sparkles size={14} className="mr-2" /> Save & enter
            </Button>
          </form>
        )}

        <p className="mt-8 font-serif-italic text-[color:var(--text-muted)] text-xs max-w-md mx-auto">
          Want more depth? Add your birth time and place on the birth chart page and we&apos;ll unlock your Moon, Rising and full natal wheel.
        </p>
      </div>
    </section>
  );
}
